import React from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, BookOpenIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
const howItWorks = [{
  icon: DownloadIcon,
  step: '1',
  title: 'Descarga Directa',
  description: 'Recibes el acceso en tu correo inmediatamente después de la compra.'
}, {
  icon: BookOpenIcon,
  step: '2',
  title: 'Escucha y Lee',
  description: 'Familiarízate con el protocolo en 10 minutos. Es claro, directo y sin relleno.'
}, {
  icon: ZapIcon,
  step: '3',
  title: 'Activa la Calma',
  description: 'Usa la Tarjeta SOS la próxima vez que sientas tensión y mira cómo tu cuerpo responde en 120 segundos.'
}];
export function TestimonialSection() {
  return <section className="py-24 px-4 sm:px-6 lg:px-8 bg-sage-light/30" data-id="element-173">
      <div className="max-w-5xl mx-auto" data-id="element-174">
        {/* How it works */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="text-center mb-16" data-id="element-175">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brown mb-4" data-id="element-176">
            ¿Cómo funciona?
          </h2>
          <p className="text-lg text-brown-light" data-id="element-177">
            Tres pasos simples para empezar hoy
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-178">
          {howItWorks.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} className="text-center relative" data-id="element-179">
                <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center text-sage mx-auto mb-5 border border-sage-light" data-id="element-180">
                  <Icon className="w-7 h-7" data-id="element-181" />
                </div>
                <div className="text-sm font-medium text-sage mb-2 uppercase tracking-wider" data-id="element-182">
                  Paso {item.step}
                </div>
                <h3 className="text-xl font-heading font-bold text-brown mb-3" data-id="element-183">
                  {item.title}
                </h3>
                <p className="text-brown-light leading-relaxed" data-id="element-184">
                  {item.description}
                </p>

                {index < howItWorks.length - 1 && <div className="hidden md:flex absolute top-8 -right-4 w-8 items-center justify-center text-sage/40" data-id="element-185">
                    <ArrowRightIcon className="w-5 h-5" data-id="element-186" />
                  </div>}
              </motion.div>;
        })}
        </div>

        {/* Before / After */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-187">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-brown" data-id="element-188">
            Tu vida antes y después
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-189">
          {/* Before */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-lg shadow-brown/5 border border-terracotta/20" data-id="element-190">
            <div className="inline-block px-3 py-1 rounded-full bg-terracotta/10 text-terracotta text-sm font-semibold mb-6 uppercase tracking-wider" data-id="element-191">
              Antes
            </div>
            <ul className="space-y-4 text-brown-light text-lg" data-id="element-192">
              <li className="flex items-start gap-3" data-id="element-193">
                <span className="text-terracotta mt-1" data-id="element-194">•</span>
                <span data-id="element-195">
                  Estar alerta todo el día, esperando el próximo ataque
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-196">
                <span className="text-terracotta mt-1" data-id="element-197">•</span>
                <span data-id="element-198">Miedo a los lugares concurridos</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-199">
                <span className="text-terracotta mt-1" data-id="element-200">•</span>
                <span data-id="element-201">Falta de aire y agotamiento mental constante</span>
              </li>
            </ul>
          </div>

          {/* After */}
          <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-lg shadow-brown/5 border border-sage/30" data-id="element-202">
            <div className="inline-block px-3 py-1 rounded-full bg-sage-light text-sage-dark text-sm font-semibold mb-6 uppercase tracking-wider" data-id="element-203">
              Después
            </div>
            <ul className="space-y-4 text-brown-light text-lg" data-id="element-204">
              <li className="flex items-start gap-3" data-id="element-205">
                <span className="text-sage mt-1" data-id="element-206">•</span>
                <span data-id="element-207">Saber exactamente qué hacer si el pánico aparece</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-208">
                <span className="text-sage mt-1" data-id="element-209">•</span>
                <span data-id="element-210">Dormir mejor y despertar con más energía</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-211">
                <span className="text-sage mt-1" data-id="element-212">•</span>
                <span data-id="element-213">Sentir que tú tienes el mando, no tus nervios</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}